import {useEffect, useRef} from "react";
import {
  adjustToTimeZone,
  ApiError,
  extractApiError,
  FleetAssetCapabilityType,
  FleetHeatmapGridRequest,
  subDays,
  useLazyGetFleetHeatmapGridQuery
} from "@ots-frontend/ots-data";
import {usePrevious} from "react-use";

export type UseFleetHeatmapProps = {
  regionId?: string;
  fromDttm?: Date | number;
  toDttm?: Date | number;
  capability?: FleetAssetCapabilityType
  minMoveMeters?: number;
  idleMs?: number;
}

export type UseFleetHeatmapResult = {
  heatmapData: google.maps.visualization.WeightedLocation[],
  isLoading: boolean,
  isFetching: boolean
  error?: ApiError
  onMapLoad: (map: google.maps.Map) => void,
  onMapUnmount: (map: google.maps.Map) => void,
  onIdle: () => void,
}

export const useFleetHeatmap = (props: UseFleetHeatmapProps): UseFleetHeatmapResult => {
  const {
    regionId,
    fromDttm,
    toDttm,
    capability,
    minMoveMeters = 500,
    idleMs = 800,
  } = props;

  const [trigger, result] = useLazyGetFleetHeatmapGridQuery();

  const mapRef = useRef<google.maps.Map | null>(null);
  const lastFetchedRef = useRef<{
    center: google.maps.LatLng;
    zoom: number;
    fromDttm: number;
    toDttm: number;
  } | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const prevBounds = usePrevious(result.originalArgs?.bounds);
  const prevZoom = usePrevious(result.originalArgs?.zoom);

  const fetchIfNeeded = () => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const center = map.getCenter();
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    if (!bounds) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const last = lastFetchedRef.current
    const timeframeChanged = !last || last.fromDttm !== fromDttm || last.toDttm !== toDttm;
    let positionChanged = false;
    if (last) {
      const distance = google.maps.geometry.spherical.computeDistanceBetween(center, last.center);
      const zoomChanged = Math.abs(zoom - last.zoom) >= 1;
      positionChanged = distance > minMoveMeters || zoomChanged;
    }

    if (timeframeChanged || positionChanged || !last) {
      const request: FleetHeatmapGridRequest = {
        regionId: regionId,
        capability: capability.code,
        fromDttm: fromDttm ? adjustToTimeZone(fromDttm).getTime() : subDays(adjustToTimeZone().getTime(), 7).getTime(),
        toDttm: toDttm ? adjustToTimeZone(toDttm).getTime() : adjustToTimeZone().getTime(),
        bounds: {
          west: sw.lng(),
          south: sw.lat(),
          east: ne.lng(),
          north: ne.lat(),
        },
        zoom: zoom,
        minCountThreshold: 1,
      }

      trigger(request, true); // prefer cache
      lastFetchedRef.current = {center, zoom, fromDttm: request.fromDttm, toDttm: request.toDttm};
    }
  }

  const scheduleFetch = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(fetchIfNeeded, idleMs);
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    fetchIfNeeded(); // initial load
  };

  const onMapUnmount = (map: google.maps.Map) => {
    mapRef.current = null;
  };

  const onIdle = () => scheduleFetch();

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // If map is already loaded, trigger immediately on timeframe change
    if (mapRef.current) {
      fetchIfNeeded();
    }
  }, [fromDttm, toDttm]);

  const heatmapData: google.maps.visualization.WeightedLocation[] =
    result.data?.cells.map((cell) => ({
      location: new google.maps.LatLng(cell.latCoord, cell.longCoord),
      weight: cell.count,
    })) ?? [];

  return {
    heatmapData: heatmapData,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error ? extractApiError(result.error) : undefined,
    onMapLoad: onMapLoad,
    onMapUnmount: onMapUnmount,
    onIdle: onIdle
  };
};
